import { useEffect, useState, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { CheckCircle2, Loader2, XCircle, ArrowRight } from "lucide-react";
import { useAuth, homeForRole } from "@/store/AuthContext";

const API = process.env.REACT_APP_BACKEND_URL;
const MAX_TRIES = 12;

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { refreshMe } = useAuth();
  const sessionId = params.get("session_id");
  const [state, setState] = useState("checking"); // checking | paid | error
  const [dest, setDest] = useState("/agenda");

  const poll = useCallback(async (tries) => {
    if (!sessionId) return setState("error");
    try {
      const r = await fetch(`${API}/api/payments/status/${sessionId}`);
      const d = await r.json();
      if (d.payment_status === "paid") {
        const u = await refreshMe();
        if (u) setDest(homeForRole(u.role));
        setState("paid");
        return;
      }
      if (d.payment_status === "expired" || d.payment_status === "failed") return setState("error");
    } catch (e) {
      // keep trying
    }
    if (tries >= MAX_TRIES) return setState("error");
    setTimeout(() => poll(tries + 1), 2000);
  }, [sessionId, refreshMe]);

  useEffect(() => { poll(0); }, [poll]);

  return (
    <div className="flex min-h-screen items-center justify-center bg-gradient-to-br from-slate-900 via-slate-800 to-blue-950 px-5">
      <div data-testid="payment-success-card" className="w-full max-w-md rounded-3xl border border-white/10 bg-white p-8 text-center shadow-2xl">
        {state === "checking" && (
          <>
            <Loader2 className="mx-auto h-10 w-10 animate-spin text-blue-600" />
            <h1 className="mt-4 font-heading text-xl font-bold text-slate-900">Confirmando seu pagamento...</h1>
            <p className="mt-1 text-sm text-slate-500">Isso leva apenas alguns segundos.</p>
          </>
        )}
        {state === "paid" && (
          <>
            <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-2xl bg-emerald-50 text-emerald-600"><CheckCircle2 className="h-8 w-8" /></div>
            <h1 className="mt-4 font-heading text-2xl font-extrabold text-slate-900">Assinatura ativada! 🎉</h1>
            <p className="mt-1 text-sm text-slate-500">Seu plano está em vigência e todos os seus dados foram mantidos.</p>
            <button data-testid="success-continue" onClick={() => navigate(dest, { replace: true })} className="mt-6 flex w-full items-center justify-center gap-2 rounded-xl bg-blue-600 py-3.5 text-sm font-bold text-white hover:bg-blue-700">
              Ir para o sistema <ArrowRight className="h-4 w-4" />
            </button>
          </>
        )}
        {state === "error" && (
          <>
            <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-2xl bg-rose-50 text-rose-600"><XCircle className="h-8 w-8" /></div>
            <h1 className="mt-4 font-heading text-xl font-bold text-slate-900">Não confirmamos o pagamento</h1>
            <p className="mt-1 text-sm text-slate-500">Se você concluiu o pagamento, aguarde um instante e tente novamente.</p>
            <button data-testid="success-retry" onClick={() => navigate("/assinar", { replace: true })} className="mt-6 w-full rounded-xl border border-slate-200 py-3 text-sm font-bold text-slate-700 hover:bg-slate-50">
              Voltar para planos
            </button>
          </>
        )}
      </div>
    </div>
  );
}
